/* @flow */

import { times, endsWith } from 'lodash';
import Formsy from 'lib/Formsy';

const ERROR_LEVELS = {
  warning: 'warning',
  error: 'error',
};

export type InputField = {
  value: string,
  errorLevel?: ?$Keys<typeof ERROR_LEVELS>,
  errorMessage?: ?string,
};

export type UserRow = {
  name: InputField,
  primaryDevice: InputField,
  secondaryDevice: InputField,
  userValid: boolean,
};

type ServerUserError = {
  address: string,
  errors: string[],
};

type errorUserCollectionGeneratorOutput = {
  remainingErrors: ServerUserError[],
  userCollection: UserRow[],
};

export const DIRECT_ADD_USER_ROLES = ['student', 'parent', 'teacher'];

export const ERROR_MESSAGES = {
  nameWarning:
    'If no name is entered, participants will be asked for their names before they’re added to your class',
  phoneInvalidError: 'Invalid phone number',
  emailRequiredError: 'Valid email required',
  deviceRequiredError: 'Valid phone number or email address required',
  deviceInvalidError: 'Invalid email address or phone number',
};

type State = {
  teachers: Array<UserRow>,
  students: Array<UserRow>,
  parents: Array<UserRow>,
  maxRows: number,
  containsData: boolean,
};

const MINIMUM_ROWS = 5;
export const MAXIMUM_ROWS = {
  group: 150,
  organization: 150,
};

export const initialState = {
  teachers: buildInitialState(),
  students: buildInitialState(),
  parents: buildInitialState(),
  maxRows: MAXIMUM_ROWS.group,
  containsData: false,
};
/* ======= */
/* REDUCER */
/* ======= */

export default function reducer(state: State = initialState, action: *): State {
  switch (action.type) {
    case 'directAddForm/HANDLE_INPUT_CHANGE': {
      const collection = `${action.role}s`;
      const currentUserCollection = state[collection];
      const updatedUserCollection = insertIntoUserCollection(
        currentUserCollection,
        action.user,
        action.key
      );

      const resizedCollection = resizeCollection(updatedUserCollection, state.maxRows);
      const stateWithNewRows = { ...state, [collection]: resizedCollection };

      return { ...updatedStateWithEmptyCheck(stateWithNewRows) };
    }
    case 'directAddForm/HANDLE_PASTE': {
      const collection = `${action.role}s`;
      const currentUserCollection = state[collection];
      const updatedUserCollection = insertIntoUserCollectionFromPaste(
        currentUserCollection,
        action.startKey,
        action.rows,
        action.emailRequired
      );

      const resizedCollection = resizeCollection(updatedUserCollection, state.maxRows);
      const stateWithNewRows = { ...state, [collection]: resizedCollection };

      return { ...updatedStateWithEmptyCheck(stateWithNewRows) };
    }
    case 'directAddForm/HANDLE_SERVER_ERRORS': {
      const { errors } = action;
      const stateWithErrors = updatedStateWithServerErrors(errors, state);

      return { ...updatedStateWithEmptyCheck(stateWithErrors) };
    }
    case 'directAddForm/SET_MAX_ROWS_FOR_ENTITY': {
      return { ...state, maxRows: action.maxRows };
    }
    case 'directAddForm/REINITIALIZE': {
      return { ...initialState };
    }
    default:
      return state;
  }
}

/* ======= */
/* ACTIONS */
/* ======= */

export type ReinitializeAction = { type: 'directAddForm/REINITIALIZE' };
export function reinitialize(): ReinitializeAction {
  return { type: 'directAddForm/REINITIALIZE' };
}

export type HandleInputChange = {
  type: 'directAddForm/HANDLE_INPUT_CHANGE',
  key: number,
  role: string,
  user: UserRow,
};

export function handleInputChange(key: number, role: string, user: UserRow): HandleInputChange {
  return {
    type: 'directAddForm/HANDLE_INPUT_CHANGE',
    key,
    role,
    user,
  };
}

export type HandlePaste = {
  type: 'directAddForm/HANDLE_PASTE',
  startKey: number,
  role: string,
  rows: string[][],
};

export function handlePaste(
  startKey: number,
  role: string,
  rows: string[][],
  emailRequired: boolean
): HandlePaste {
  return {
    type: 'directAddForm/HANDLE_PASTE',
    startKey,
    role,
    rows,
    emailRequired,
  };
}

export type SetMaxRowsForEntity = {
  type: 'directAddForm/SET_MAX_ROWS_FOR_ENTITY',
  maxRows: number,
};

export function setMaxRowsForEntity(entity: 'group' | 'organization'): SetMaxRowsForEntity {
  const maxRows = MAXIMUM_ROWS[entity];
  return {
    type: 'directAddForm/SET_MAX_ROWS_FOR_ENTITY',
    maxRows,
  };
}

export type HandleServerErrors = {
  type: 'directAddForm/HANDLE_SERVER_ERRORS',
  errors: ServerUserError[],
};

export function handleServerErrors(errors: ServerUserError[]): HandleServerErrors {
  return {
    type: 'directAddForm/HANDLE_SERVER_ERRORS',
    errors,
  };
}

/* ======= */
/* HELPERS */
/* ======= */

export function userRowEmpty(user: UserRow): boolean {
  return (
    user.name.value.length === 0 &&
    user.primaryDevice.value.length === 0 &&
    user.secondaryDevice.value.length === 0
  );
}

function emptyUserRow(): UserRow {
  return {
    name: {
      value: '',
    },
    primaryDevice: {
      value: '',
    },
    secondaryDevice: {
      value: '',
    },
    userValid: true,
  };
}

function buildInitialState(): Array<UserRow> {
  return times(5, () => emptyUserRow());
}

function insertIntoUserCollection(
  currentUserCollection: Array<UserRow>,
  newUser: UserRow,
  index: number
): Array<UserRow> {
  const updatedUserCollection = [...currentUserCollection];
  updatedUserCollection[index] = newUser;

  return updatedUserCollection;
}

function resizeCollection(collection: UserRow[], maxRows: number): UserRow[] {
  const trimmedRows = trimRows(collection, maxRows);
  return padRows(trimmedRows, maxRows);
}

// Remove all the empty rows from the end
function trimRows(rows: Array<UserRow>, maxRows: number): Array<UserRow> {
  const lastIndex = rows.length - 1;
  for (let i = lastIndex; i >= MINIMUM_ROWS - 1; i--) {
    if (userRowEmpty(rows[i])) {
      rows.pop();
    } else {
      break;
    }
  }
  return rows.slice(0, maxRows);
}

function padRows(rows: Array<UserRow>, maxRows: number): Array<UserRow> {
  const rowsUnderMinimum = MINIMUM_ROWS - rows.length;
  const collectionWithinMaxLimit = rows.length < maxRows;

  if (rowsUnderMinimum > 0) {
    times(rowsUnderMinimum, () => rows.push(emptyUserRow()));
  } else if (collectionWithinMaxLimit) {
    rows.push(emptyUserRow());
  }
  return rows;
}

function insertIntoUserCollectionFromPaste(
  currentUserCollection: UserRow[],
  startingIndex: number,
  rows: string[][],
  emailRequired: boolean
): UserRow[] {
  const updatedUserCollection = [...currentUserCollection];

  const pastedUserRows = packagePastedDataIntoUserRows(rows, emailRequired);

  // Insert the users rows at the correct place in the collection
  updatedUserCollection.splice(startingIndex, pastedUserRows.length, ...pastedUserRows);

  return updatedUserCollection;
}

function packagePastedDataIntoUserRows(rows: string[][], emailRequired: boolean): UserRow[] {
  return rows.map(row => {
    const nameValue = row[0].trim();
    const primaryDeviceValue = (row[1] || '').trim();
    const secondaryDeviceValue = (row[2] || '').trim();

    const inputtedRow = {
      name: packageInputField(nameValue),
      primaryDevice: packageInputField(primaryDeviceValue),
      secondaryDevice: packageInputField(secondaryDeviceValue),
      userValid: false,
    };

    return addValidationErrors(inputtedRow, emailRequired);
  });
}

export function addValidationErrors(
  userRow: UserRow,
  emailRequired: boolean,
  primaryDeviceInFocus: boolean = false
): UserRow {
  const name = userRow.name.value;
  const primaryDevice = userRow.primaryDevice.value;
  const secondaryDevice = userRow.secondaryDevice.value;

  const nameWarning = getNameWarningMessage(name, primaryDevice);
  // Don't show an error in the primary device field if cursor is there
  const primaryDeviceError =
    (name || primaryDevice || secondaryDevice) && !primaryDeviceInFocus
      ? getPrimaryDeviceError(primaryDevice, emailRequired)
      : null;

  const secondaryDeviceError = secondaryDevice
    ? getSecondaryDeviceError(secondaryDevice, emailRequired)
    : null;

  const userValid = !(primaryDeviceError || secondaryDeviceError);

  return {
    ...userRow,
    name: packageInputField(name, nameWarning, nameWarning ? ERROR_LEVELS.warning : null),
    primaryDevice: packageInputField(
      primaryDevice,
      primaryDeviceError,
      primaryDeviceError ? ERROR_LEVELS.error : null
    ),
    secondaryDevice: packageInputField(
      secondaryDevice,
      secondaryDeviceError,
      secondaryDeviceError ? ERROR_LEVELS.error : null
    ),
    userValid,
  };
}

function packageInputField(
  value: string,
  errorMessage?: ?string,
  errorLevel?: ?$Keys<typeof ERROR_LEVELS>
): InputField {
  return {
    value,
    errorMessage,
    errorLevel,
  };
}

function getNameWarningMessage(name: string, primaryDevice: string) {
  return !name && primaryDevice ? ERROR_MESSAGES.nameWarning : null;
}

function getPrimaryDeviceError(device: string, emailRequired: boolean): ?string {
  if (emailRequired) {
    return isEmailValid(device) ? null : ERROR_MESSAGES.emailRequiredError;
  } else {
    return isDeviceValid(device) ? null : ERROR_MESSAGES.deviceRequiredError;
  }
}

function getSecondaryDeviceError(device: string, emailRequired: boolean): ?string {
  if (emailRequired) {
    return isPhoneValid(device) ? null : ERROR_MESSAGES.phoneInvalidError;
  } else {
    return isDeviceValid(device) ? null : ERROR_MESSAGES.deviceInvalidError;
  }
}

function isDeviceValid(device: string) {
  return isEmailValid(device) || isPhoneValid(device);
}

function isEmailValid(email: string) {
  return Formsy.customValidators.isEmailWithSpaces({}, email);
}

function isPhoneValid(phone: string) {
  return Formsy.customValidators.couldPlausiblyBeANorthAmericanPhoneNumber({}, phone);
}

function someRowFilled(students: UserRow[], parents: UserRow[], teachers: UserRow[]): boolean {
  return (
    students.some(userRow => !userRowEmpty(userRow)) ||
    parents.some(userRow => !userRowEmpty(userRow)) ||
    teachers.some(userRow => !userRowEmpty(userRow))
  );
}

function updatedStateWithEmptyCheck(state: State): State {
  const containsData = someRowFilled(state.students, state.parents, state.teachers);

  return { ...state, containsData };
}

function updatedStateWithServerErrors(errors: ServerUserError[], state: State): State {
  const studentErrorCollection = generateErrorUserCollection(errors, state.students);
  const parentErrorCollection = generateErrorUserCollection(
    studentErrorCollection.remainingErrors,
    state.parents
  );
  const teacherErrorCollection = generateErrorUserCollection(
    parentErrorCollection.remainingErrors,
    state.teachers
  );

  if (teacherErrorCollection.remainingErrors.length > 0) {
    console.error('directAddForm not all server errors matched');
  }

  const students = resizeCollection(studentErrorCollection.userCollection, state.maxRows);
  const parents = resizeCollection(parentErrorCollection.userCollection, state.maxRows);
  const teachers = resizeCollection(teacherErrorCollection.userCollection, state.maxRows);

  return { ...state, students, parents, teachers };
}

export function generateErrorUserCollection(
  errors: ServerUserError[],
  collection: UserRow[]
): errorUserCollectionGeneratorOutput {
  return errors.reduce(
    ({ remainingErrors, userCollection }, error) => {
      // Removes sms://+1 or email:// from the address string
      const errorAddress = error.address.replace(/.*\/\/(\+1)*/, '');
      const matchingUser = collection.find(user => {
        // We strip the +1 out of numbers above since most users enter numbers without them.
        // Since there is a small chance of users entering #s with +1, strip them out here as well.
        const normalizedPrimaryDevice = user.primaryDevice.value.replace(/^\+1/, '');
        const normalizedSecondaryDevice = user.secondaryDevice.value.replace(/^\+1/, '');
        return (
          errorAddress === normalizedPrimaryDevice || errorAddress === normalizedSecondaryDevice
        );
      });

      if (matchingUser) {
        userCollection.push(buildErrorUser(matchingUser, error));

        // Remove error from array
        const errorIndex = remainingErrors.indexOf(error);
        remainingErrors.splice(errorIndex, 1);
      }

      return { remainingErrors, userCollection };
    },
    { remainingErrors: [...errors], userCollection: [] }
  );
}

function buildErrorUser(user: UserRow, error: ServerUserError): UserRow {
  // Determine which of the two devices on UserRow has the error
  const deviceType = endsWith(error.address, user.primaryDevice.value)
    ? 'primaryDevice'
    : 'secondaryDevice';

  const deviceWithError = {
    ...user[deviceType],
    errorLevel: 'error',
    errorMessage: error.errors[0],
  };

  return {
    ...user,
    [deviceType]: deviceWithError,
    userValid: false,
  };
}
